import { boot } from 'quasar/wrappers'
import { api } from 'boot/axios'
import { computed, ref } from 'vue'
import { config } from 'boot/config'
import { empty } from 'src/utils/variables'
import logger from 'src/services/logger/index.js'

export const ACCOUNT_STATES = {
  LOGGED_IN: 'LOGGED_IN',
  NOT_LOGGED_IN: 'NOT_LOGGED_IN',
  MFA_REQUIRED: 'MFA_REQUIRED',
  EMAIL_VERIFICATION_REQUIRED: 'EMAIL_VERIFICATION_REQUIRED',
  MOBILE_PHONE_VERIFICATION_REQUIRED: 'MOBILE_PHONE_VERIFICATION_REQUIRED',
  CGU_CONSENT_REQUIRED: 'CGU_CONSENT_REQUIRED',
  PASSWORD_EXPIRED: 'PASSWORD_EXPIRED',
  PASSWORD_CREATION_REQUIRED: 'PASSWORD_CREATION_REQUIRED',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
}
const ERROR_TO_ACCOUNT_STATE = {
  two_factor_authentication_required: ACCOUNT_STATES.MFA_REQUIRED,
  email_not_verified: ACCOUNT_STATES.EMAIL_VERIFICATION_REQUIRED,
  mobile_phone_not_verified: ACCOUNT_STATES.MOBILE_PHONE_VERIFICATION_REQUIRED,
  cgu_not_accepted: ACCOUNT_STATES.CGU_CONSENT_REQUIRED,
  password_expired: ACCOUNT_STATES.PASSWORD_EXPIRED,
  password_creation_required: ACCOUNT_STATES.PASSWORD_CREATION_REQUIRED
}

const loading = ref(false)
const accountState = ref(ACCOUNT_STATES.NOT_LOGGED_IN)
const user = ref()
const mfaUserIdentifier = ref()
const mfaEmailHint = ref()
const mfaSmsHint = ref()
const emailVerificationEmail = ref()
const mobilePhoneBeingVerified = ref()
const mfaSmsEnabled = ref(false)
const mfaProviders = ref([])
const targetUri = ref()
const isEnrollBiometry = ref(false)
const identityProvider = ref((new URLSearchParams(window.location.search).get('identity_provider')))

// TODO Use roles instead
export const isAdmin = computed(() => user.value.type === 'person')

async function checkSession() {
  try {
    auth.loading.value = true
    const { data } = await api.get('/api/me')
    if(!empty(identityProvider.value) && (empty(data.identityProviders) || !data.identityProviders.includes(identityProvider.value))) {
      auth.loading.value = false
      return
    }

    auth.user.value = data
    accountState.value = ACCOUNT_STATES.LOGGED_IN

    if (isEnrollBiometry.value) {
      auth.loading.value = false
      return
    }

    if (targetUri.value && new URL(config.api.baseURL).origin === new URL(targetUri.value, config.api.baseURL).origin) {
      window.location.replace(targetUri.value)
      return
    }

    auth.loading.value = false
  } catch (error) {
    auth.loading.value = false
    auth.user.value = null
    logger.error(error)
  }
}

function logout(targetUri) {
  window.location.assign(config.api.baseURL + '/api/logout' + (empty(targetUri) ? '' : '?target_uri=' + encodeURIComponent(targetUri)))
}

const auth = {
  loading,
  accountState,
  user,
  targetUri,
  mfaUserIdentifier,
  mfaEmailHint,
  mfaSmsHint,
  mfaSmsEnabled,
  emailVerificationEmail,
  mobilePhoneBeingVerified,
  mfaProviders,
  isAdmin,
  logout,
  checkSession,
  isEnrollBiometry
}

export default boot(async ({ app }) => {
  app.config.globalProperties.$auth = auth

  api.interceptors.response.use(response => response, error => {
    const data = error.response.data
    const newAccountState = !empty(data.error) && data.error in ERROR_TO_ACCOUNT_STATE ? ERROR_TO_ACCOUNT_STATE[data.error] : null

    if (newAccountState) {
      accountState.value = newAccountState

      if (newAccountState === ACCOUNT_STATES.MFA_REQUIRED) {
        mfaUserIdentifier.value = data.user_identifier
        mfaProviders.value = data.two_factor_providers
        mfaEmailHint.value = data.email_hint || null
        mfaSmsHint.value = data.sms_hint || null
        mfaSmsEnabled.value = data.sms_enabled || false
      } else if (newAccountState === ACCOUNT_STATES.EMAIL_VERIFICATION_REQUIRED) {
        emailVerificationEmail.value = data.email || null
      } else if (newAccountState === ACCOUNT_STATES.MOBILE_PHONE_VERIFICATION_REQUIRED) {
        mobilePhoneBeingVerified.value = data.mobile_phone || null
      }
    }

    return Promise.reject(error)
  })

  targetUri.value = (new URLSearchParams(window.location.search)).get('target_uri')
  await checkSession()
})

export { auth }
